import type { SearchEngineLocation } from '~/composables/types/searchEngine'

// Add a type discriminator to each location slug so that options with the same
// slug but different types (e.g. "Berlin" city vs "Berlin" region) are treated
// as distinct entries by FilterItem's selection logic.
const LOCATION_TYPE_SEPARATOR = '!'

export const useLocationTypeSlug = () => {
  const encodeLocationSlug = (slug: string, type?: string) =>
    `${slug}${LOCATION_TYPE_SEPARATOR}${type ?? 'city'}`

  const decodeLocationSlug = (
    raw: string
  ): { slug: string; locationType: string | undefined } => {
    const sepIdx = raw.lastIndexOf(LOCATION_TYPE_SEPARATOR)
    const slug = sepIdx !== -1 ? raw.slice(0, sepIdx) : raw
    // The suffix is the raw Algolia type ('city' | 'region' | 'admin_area' | …)
    const rawType = sepIdx !== -1 ? raw.slice(sepIdx + 1) : undefined
    // Only return a non-city locationType — 'city' is the default query behaviour
    // (city_slug filter), so returning it explicitly would not change the Algolia
    // query and would make it look like the search didn't retrigger.
    const locationType = rawType !== 'city' ? rawType : undefined
    return { slug, locationType }
  }

  const toLocationOptions = (localities: SearchEngineLocation[] | undefined) =>
    localities?.map((loc) => ({
      ...loc,
      // Composite slug keeps the option unique per type so FilterItem can
      // distinguish "Berlin city" from "Berlin region" even when slug is identical.
      slug: encodeLocationSlug(loc.slug, loc.type),
    })) ?? []

  return { encodeLocationSlug, decodeLocationSlug, toLocationOptions }
}
